// useReturnUrl (SIM-3 / ACS-4127): extracts the returnUrl query param on mount, runs it
// through the shared validator, and exposes the VALIDATED value plus a redirect callback.
// Marketing/referral params are preserved across the redirect (BR-018/BR-043). SSR-safe:
// window/sessionStorage are only touched inside the effect/callback.
import { useEffect, useState, useCallback } from 'react';
import { validateReturnUrl } from 'utils/returnUrlValidator';
import LanguageLocale from 'utils/LanguageLocale';
import Url from 'utils/Url';
// Marketing (BR-018) + referral (BR-043) params carried through the sign-in redirect.
// Shared with the SIM-11 choke-point (navigateToSignIn) via the extracted module (drift prevention).
import { MARKETING_KEYS, STORAGE_KEY, MAX_PARAM_LEN } from 'utils/signInMarketingParams';

/**
 * Read and remove the preserved marketing params from sessionStorage. Never throws when
 * storage is unavailable (private mode) — returns an empty object instead.
 * @returns {Object} The preserved marketing params (possibly empty).
 */
const consumePreservedParams = () => {
    let preserved = {};

    try {
        const raw = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}');
        // JSON.parse can legitimately yield null / a primitive / an array (sessionStorage is writable
        // by other code). Only a plain object is a valid preserved-params map — coerce anything else to
        // {} so the helper keeps its "never throws, returns an object" contract and the downstream
        // Object.entries() in redirectToReturnUrl is always safe.
        preserved = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
    } catch (e) {
        preserved = {};
    }

    try {
        window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
        // storage unavailable: nothing to clear.
    }

    return preserved;
};

/**
 * Hook that extracts the `returnUrl` query param, validates it against the open-redirect
 * control, and exposes `{ returnUrl, redirectToReturnUrl }`. `returnUrl` is always the
 * validated (never raw) value; `redirectToReturnUrl` navigates via `utils/Url.redirectTo`.
 * @returns {{ returnUrl: string, redirectToReturnUrl: function }}
 */
const useReturnUrl = () => {
    const [returnUrl, setReturnUrl] = useState('/');

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const raw = params.get('returnUrl');

        let locale;

        try {
            locale = LanguageLocale.getCurrentLanguageCountryCode();
        } catch (e) {
            // SSR/early-boot: locale globals may be uninitialized and this can throw. Pass undefined so
            // validateReturnUrl/localeHomepage (already guarded) derive the safe fallback instead of
            // letting the mount effect throw.
            locale = undefined;
        }

        setReturnUrl(validateReturnUrl(raw, locale));

        const preserved = {};

        MARKETING_KEYS.forEach(key => {
            const value = params.get(key);

            if (value && value.length <= MAX_PARAM_LEN) {
                preserved[key] = value;
            }
        });

        try {
            if (Object.keys(preserved).length) {
                window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(preserved));
            } else {
                // No marketing params on this visit — clear any stale values a previous sign-in
                // attempt left behind, so the redirect reflects only the current URL.
                window.sessionStorage.removeItem(STORAGE_KEY);
            }
        } catch (e) {
            // storage unavailable (private mode): fall back to URL-only, do not throw.
        }
    }, []);

    const redirectToReturnUrl = useCallback(() => {
        const preserved = consumePreservedParams();
        const target = new URL(returnUrl, window.location.origin);

        Object.entries(preserved).forEach(([key, value]) => {
            target.searchParams.set(key, value);
        });

        // Same-origin navigation only (host intentionally dropped). An allowlisted EXTERNAL
        // partner returnUrl (OAuth2 redirect_uri) is validated/accepted at the validator layer,
        // but cross-origin navigation from here is owned by SIM-13 (ACS-4137) — tracked as MED-1.
        Url.redirectTo(target.pathname + target.search + target.hash);
    }, [returnUrl]);

    return { returnUrl, redirectToReturnUrl };
};

export { useReturnUrl };
